import * as THREE from 'three';
import { HUMAN_NECK_BASE_RISE, type HumanProportions } from '../../../content/characters/rigs/human.js';
import { SKIN_VOLUME } from './volume-skin.js';
import { cutBodyGarment } from './body-garment.js';
import { taped } from './figure-tape.js';

type Blend = [string, string, number];
type Influence = [number, number][];
type Region = 'trunk' | 'neck' | 'arm' | 'leg' | 'hand' | 'foot';
interface Surface {
  /** Ascending rows, each with a repeated final vertex at the angular seam. */
  grid: THREE.Vector3[][];
  weights: (point: THREE.Vector3) => Blend;
}
export interface ConnectedBodyOptions {
  proportions: HumanProportions;
  root: THREE.Object3D;
  skeleton: THREE.Skeleton;
  joints: Map<string, THREE.Bone>;
  /** Only sample/sculpt the rest surface when its anatomy is absent from the cache. */
  surfaces: () => { torso: Surface; arms: [Surface, Surface]; legs: [Surface, Surface] };
  relief: (point: THREE.Vector3) => THREE.Vector3 | undefined;
  /** Includes anatomy, resolution and bone ordering; omitted for uncached authoring callers. */
  cacheKey?: string;
}
export interface ConnectedBody {
  geometry: THREE.BufferGeometry;
  /** `shoulder` picks the trunk's half of the faces bridging a shoulder port to its arm (part of `arm`), so a top can cover them without the arm. */
  /** How near the midline, at a height on the front or the back, the edge of a covered shoulder bridge (see `SHOULDER_COVER`) comes:
   * where a top carried over the bridges ends. */
  coverAt: (y: number, back: boolean) => number;
  garment: (regions: (Region | 'shoulder')[], from: number, to: number, gap: number, side?: number, cuts?: ((point: THREE.Vector3) => number)[]) => THREE.BufferGeometry;
  /** Partition the connected surface by material, with identical positions and weights at each seam.
   * `hidden` drops a region's triangles above a height, where a long skirt covers them and a stride would only push them through it. */
  dress: (materials: Record<Region, THREE.Material>, clearances: Partial<Record<Region, number>>, hidden?: Partial<Record<Region, number>>) => { material: THREE.Material; geometry: THREE.BufferGeometry }[];
}
const cache = new Map<string, { geometry: THREE.BufferGeometry; regions: Region[]; shoulders: Set<number>; along: Float32Array }>();
const CACHE_LIMIT = 16;
/** A port's edges rise and fall by these shares of its height at its front and back ends, eased over `PORT_REACH` of its height
 * above and below and `PORT_FADE` of its half width beyond. */
const PORT_LIFT = [.45, .25], PORT_REACH = .8, PORT_FADE = .6;
/** How far a bridge leaves the chest along the trunk's own slope, as a share of its length, under and beside the arm. */
const CHEST_SLOPE = .35;
/** A top that covers the shoulder bridges stops this share of the way from the chest port to the arm. */
const SHOULDER_COVER = .3;
/** The shoulder field's extent: over the trunk, as a share of the socket's distance from the midline, and down the upper arm.
 * Normals are softened over the bridge and `SOFT_HOPS` rings around it. */
const FIELD_REACH = 1.1, FIELD_ARM = .55, SOFT_HOPS = 3, SOFT_PASSES = 8, FIELD_PASSES = 200, FIELD_RELAX = 1.85;
/** Around the deltoid and over the top of the shoulder the turn falls off evenly with the angle from the arm, from one within
 * `TURN_CONE` of it to zero at `TURN_TOP`. Any steeper, and skin pressed together by an arm raised to Y folds over itself.
 * `TURN_PASSES` relax it afterwards, so the armpit does not pit where the spread meets the solved field. */
const TURN_CONE = THREE.MathUtils.degToRad(20), TURN_TOP = THREE.MathUtils.degToRad(185), TURN_PASSES = 12;
/** How much of the armpit, which faces the trunk and stretches as the arm rises, is skinned linearly rather than by volume. */
const ARMPIT_LINEAR = .6;

/**
 * Indexed branching skin. Shoulder ports replace trunk faces; the pelvis and palm divide into
 * shared crotch/web edges. Limbs, digits and feet are stitched to these ports, without internal caps.
 * All work happens during construction; the existing skeleton deforms the result on the GPU.
 */
export function buildConnectedBody(options: ConnectedBodyOptions): ConnectedBody {
  // A baked figure replays the sculpted surface (`figure-tape.ts`).
  const { geometry, regions, shoulders, along } = taped('skin', () => connectedSurface(options));
  return bodyResult(geometry, regions, shoulders, along, options.proportions.socketZ);
}
function connectedSurface(options: ConnectedBodyOptions): { geometry: THREE.BufferGeometry; regions: Region[]; shoulders: Set<number>; along: Float32Array } {
  const cached = options.cacheKey ? cache.get(options.cacheKey) : undefined;
  if (cached) {
    cache.delete(options.cacheKey!); cache.set(options.cacheKey!, cached);
    return { ...cached, geometry: cached.geometry.clone() };
  }
  const { proportions: p, skeleton, joints, root } = options;
  const surfaces = options.surfaces();
  root.updateMatrixWorld(true);
  const inverseRoot = root.matrixWorld.clone().invert();
  const boneIndex = new Map(skeleton.bones.map((bone, i) => [bone.name, i]));
  const matrix = (name: string) => inverseRoot.clone().multiply(joints.get(name)!.matrixWorld);
  const rigid = (name: string): Influence => [[boneIndex.get(name)!, 1]];
  const blend = ([a, b, t]: Blend): Influence => [[boneIndex.get(a)!, 1 - t], [boneIndex.get(b)!, t]];
  const mix = (a: Influence, b: Influence, t: number): Influence => {
    const amounts = new Map<number, number>();
    for (const [list, amount] of [[a, 1 - t], [b, t]] as const) for (const [bone, weight] of list) amounts.set(bone, (amounts.get(bone) ?? 0) + weight * amount);
    const retained = [...amounts].filter(([, weight]) => weight > 1e-8).sort((a, b) => b[1] - a[1]).slice(0, 4);
    const total = retained.reduce((sum, [, weight]) => sum + weight, 0);
    return retained.map(([bone, weight]) => [bone, weight / total]);
  };
  const points: THREE.Vector3[] = [], light: THREE.Vector3[] = [], weights: Influence[] = [], triangles: number[] = [], regions: Region[] = [];
  /** Each point as it would be with the shoulder dimensions at 1: the shoulder field is laid out on it, so those controls reshape the deltoid without changing how it bends. */
  const plain: THREE.Vector3[] = [], armpits = new Map<number, number>();
  const edges = new Map<string, { from: number; uses: number }>(), soften = new Map<number, number>();
  const edgeKey = (a: number, b: number) => a < b ? `${a}:${b}` : `${b}:${a}`;
  const vertex = (point: THREE.Vector3, influence: Influence, relief = options.relief(point)) => {
    points.push(point); plain.push(point.clone()); light.push(point.clone().add(relief ?? new THREE.Vector3())); weights.push(influence); return points.length - 1;
  };
  const face = (a: number, b: number, c: number, region: Region = 'trunk') => {
    triangles.push(a, b, c); regions.push(region);
    for (const [from, to] of [[a, b], [b, c], [c, a]]) {
      const key = edgeKey(from, to), edge = edges.get(key);
      if (edge) edge.uses++; else edges.set(key, { from, uses: 1 });
    }
  };
  const quad = (a: number, b: number, c: number, d: number, region: Region = 'trunk') => { face(a, b, c, region); face(a, c, d, region); };
  const cap = (ring: number[], region: Region) => {
    const centre = ring.reduce((sum, id) => sum.add(points[id]), new THREE.Vector3()).divideScalar(ring.length);
    const id = vertex(centre, weights[ring[0]]);
    for (let i = 0; i < ring.length; i++) face(id, ring[i], ring[(i + 1) % ring.length], region);
  };
  const boundaryDirection = (ring: number[]) => {
    for (let i = 0; i < ring.length; i++) {
      const edge = edges.get(edgeKey(ring[i], ring[(i + 1) % ring.length]));
      if (edge?.uses === 1) return edge.from === ring[i] ? 1 : -1;
    }
    return 0;
  };
  /** Existing boundary winding constrains the match: distance alone can turn an entire limb inside out. */
  const align = (a: number[], b: number[]) => {
    let best = Infinity, matched = b;
    const required = -boundaryDirection(a) * boundaryDirection(b);
    for (const direction of required ? [required] : [-1, 1]) for (let phase = 0; phase < b.length; phase++) {
      const candidate = b.map((_, i) => b[(phase + direction * i + b.length) % b.length]);
      let cost = 0;
      for (let i = 0; i < a.length; i++) cost += points[a[i]].distanceToSquared(points[candidate[Math.floor(i * b.length / a.length)]]);
      if (cost < best) { best = cost; matched = candidate; }
    }
    return matched;
  };
  const stitch = (a: number[], input: number[], region: Region = 'trunk', matched = false) => {
    const aDirection = boundaryDirection(a), b = matched ? input : align(a, input);
    const flip = aDirection ? aDirection < 0 : boundaryDirection(b) > 0;
    const emit = (x: number, y: number, z: number) => flip ? face(x, z, y, region) : face(x, y, z, region);
    let i = 0, j = 0;
    while (i < a.length || j < b.length) {
      const av = a[i % a.length], bv = b[j % b.length];
      if (i < a.length && (j === b.length || (i + 1) / a.length <= (j + 1) / b.length)) { emit(av, bv, a[(i + 1) % a.length]); i++; }
      else { emit(av, bv, b[(j + 1) % b.length]); j++; }
    }
  };
  /** Add a transition row with blended weights instead of a hard rigid attachment. */
  const join = (a: number[], input: number[], region: Region = 'trunk') => {
    const b = align(a, input), middle = b.map((id, i) => {
      const at = i * a.length / b.length, low = Math.floor(at), t = at - low;
      const near = a[low], next = a[(low + 1) % a.length];
      const point = points[near].clone().lerp(points[next], t).lerp(points[id], .5);
      return vertex(point, mix(mix(weights[near], weights[next], t), weights[id], .5));
    });
    stitch(a, middle, region); stitch(middle, b, region);
  };
  const rows = (surface: Surface) => surface.grid.map(row => row.slice(0, -1).map(point => vertex(point, blend(surface.weights(point)))));
  const tube = (grid: number[][], region: Region = 'trunk', skip?: (row: number, column: number) => boolean) => {
    const columns = grid[0].length;
    for (let r = 0; r < grid.length - 1; r++) for (let c = 0; c < columns; c++) if (!skip?.(r, c)) quad(grid[r][c], grid[r + 1][c], grid[r + 1][(c + 1) % columns], grid[r][(c + 1) % columns], region);
  };
  /** Boundary of a rectangular hole on a wrapped grid. End coordinates refer to vertices. `mirror` walks the hole mirrored
   * across the grid's half turn, so a port on the far side starts and runs as the reflection of this one. */
  const port = (grid: number[][], fromRow: number, toRow: number, fromColumn: number, toColumn: number, mirror = false, outside?: number[][]) => {
    const ids: number[] = [], n = grid[0].length, at = (r: number, c: number) => grid[r][((mirror ? n / 2 - c : c) % n + n) % n];
    // `outside` collects each boundary vertex's grid neighbours just beyond the hole (both, at a corner).
    const add = (r: number, c: number, ...beyond: [number, number][]) => { ids.push(at(r, c)); outside?.push(beyond.map(([row, column]) => at(row, column))); };
    for (let c = fromColumn; c < toColumn; c++) add(fromRow, c, [fromRow - 1, c], ...(c === fromColumn ? [[fromRow, c - 1] as [number, number]] : []));
    for (let r = fromRow; r < toRow; r++) add(r, toColumn, [r, toColumn + 1], ...(r === fromRow ? [[r - 1, toColumn] as [number, number]] : []));
    for (let c = toColumn; c > fromColumn; c--) add(toRow, c, [toRow + 1, c], ...(c === toColumn ? [[toRow, c + 1] as [number, number]] : []));
    for (let r = toRow; r > fromRow; r--) add(r, fromColumn, [r, fromColumn - 1], ...(r === toRow ? [[r + 1, fromColumn] as [number, number]] : []));
    return ids;
  };
  const trunk = rows(surfaces.torso), count = trunk[0].length, shoulderY = p.shoulderY + p.socketY;
  const closestRow = (y: number) => surfaces.torso.grid.reduce((best, row, i, grid) => Math.abs(row[0].y - y) < Math.abs(grid[best][0].y - y) ? i : best, 0);
  // Leave a row outside each port for its boundary slopes, even at extreme authoring proportions.
  const low = THREE.MathUtils.clamp(closestRow(shoulderY - .08), 1, trunk.length - 3),
  high = THREE.MathUtils.clamp(closestRow(Math.min(shoulderY + .08, p.shoulderY + HUMAN_NECK_BASE_RISE)), low + 1, trunk.length - 2);
  // Reach the neck-side collarbone using the actual chest/neck dimensions.
  // Leave shared front/back strips so both shoulder ports stay separate.
  const chestWidth = Math.max(...surfaces.torso.grid.slice(low, high + 1).flat().map(point => Math.abs(point.x)));
  const reachAngle = Math.acos(THREE.MathUtils.clamp(p.neckRadius * .95 / chestWidth, 0, 1));
  const spread = Math.max(2, Math.min(Math.floor(count / 4) - 2, Math.round(reachAngle / (2 * Math.PI) * count)));
  const shoulders = [count / 2, 0].map(centre => ({ start: centre - spread, end: centre + spread }));
  // Bend the trunk's rows around each port so its lower and upper edges curve in towards the front and back. The bridge then
  // leaves the chest along an oval instead of a straight line drawn across the pectoral and the back.
  const restRows = trunk.map(row => row.map(id => [points[id].clone(), light[id].clone()] as const)), span = high - low;
  const reach = Math.max(1, Math.round(span * PORT_REACH));
  // Where the two ports' fades meet across the front and back strips, a column takes the stronger lift, so both sides bend alike.
  const lifts = Array.from({ length: count }, (_, c) => Math.max(...shoulders.map(({ start, end }) => {
    const half = (end - start) / 2, u = Math.abs(((c - start - half) % count + count * 1.5) % count - count / 2) / half;
    return u <= 1 ? THREE.MathUtils.smoothstep(u, .2, 1) : 1 - THREE.MathUtils.smoothstep(u, 1, 1 + PORT_FADE);
  })));
  lifts.forEach((lift, c) => {
    if (!lift) return;
    const bottom = lift * span * PORT_LIFT[0], top = lift * span * PORT_LIFT[1], from = Math.max(0, low - reach), to = Math.min(trunk.length - 1, high + reach);
    for (let r = from; r <= to; r++) {
      const at = r < low ? THREE.MathUtils.mapLinear(r, from, low, from, low + bottom)
        : r <= high ? THREE.MathUtils.mapLinear(r, low, high, low + bottom, high - top)
        : THREE.MathUtils.mapLinear(r, high, to, high - top, to);
      const row = Math.min(Math.floor(at), trunk.length - 2), f = at - row, [a, aLight] = restRows[row][c], [b, bLight] = restRows[row + 1][c];
      points[trunk[r][c]].lerpVectors(a, b, f); plain[trunk[r][c]].lerpVectors(a, b, f); light[trunk[r][c]].lerpVectors(aLight, bLight, f);
    }
  });
  tube(trunk, 'trunk', (r, c) => r >= low && r < high && shoulders.some(({ start, end }) => {
    const offset = (c - start + count) % count; return offset < end - start;
  }));
  cap(trunk[trunk.length - 1], 'neck'); // Hidden inside the separately swappable head.
  // How far along its shoulder bridge each vertex lies, from the chest port (0) to the arm (1).
  const bridges = new Set<number>(), bridgeAlong = new Map<number, number>();
  for (let side = 0; side < 2; side++) {
    const grid = rows(surfaces.arms[side]);
    tube(grid, 'arm');
    const first = regions.length;
    // The left port is walked as the mirror image of the right one, so both bridges are sampled alike.
    const { start, end } = shoulders[1], outside: number[][] = [];
    const bridge = buildShoulder(side === 0 ? 'L' : 'R', side === 0 ? -1 : 1, port(trunk, low, high, start, end, side === 0, outside), outside, grid);
    for (let t = first; t < regions.length; t++) bridges.add(t);
    buildHand(side === 0 ? 'L' : 'R', side === 0 ? -1 : 1, grid[0]);
    shoulderField(side === 0 ? 'L' : 'R', side === 0 ? -1 : 1, grid, bridge, surfaces.arms[side]);
  }

  /**
   * How far each vertex around a shoulder turns with its arm, as one smooth field over trunk, bridge and arm:
   * zero across the chest and back away from the shoulder, one down the upper arm, and harmonic in between,
   * so the bend spreads over the pectoral, the back and the deltoid instead of folding at a seam. On the arm and the outer
   * bridge it is never less than the share set by the angle from the arm, so the deltoid closes up evenly over the top.
   */
  function shoulderField(suffix: string, side: number, armGrid: number[][], bridge: number[], arm: Surface) {
    const pivot = new THREE.Vector3().setFromMatrixPosition(matrix(`upper_arm.${suffix}`));
    const socket = side * pivot.x, reach = socket * FIELD_REACH, armReach = p.upperArm * FIELD_ARM;
    const neckBase = p.shoulderY + HUMAN_NECK_BASE_RISE, share = new Map<number, number>(), free: number[] = [];
    for (const row of trunk) for (const id of row) {
      const point = plain[id];
      share.set(id, 0);
      if (side * point.x >= p.neckRadius * .9 && point.y <= neckBase && point.distanceTo(pivot) <= reach) free.push(id);
    }
    for (const row of armGrid) for (const id of row) {
      share.set(id, 1);
      if (plain[id].y >= pivot.y - armReach) free.push(id);
    }
    for (const id of bridge) { share.set(id, .5); free.push(id); }
    const around = new Map<number, [number, number][]>(free.map(id => [id, []]));
    for (const key of edges.keys()) {
      const [a, b] = key.split(':').map(Number), length = 1 / Math.max(1e-4, plain[a].distanceTo(plain[b]));
      around.get(a)?.push([b, length]); around.get(b)?.push([a, length]);
    }
    // Shading is smoothed over the bridge and a few rings either side of it, so neither the chest port nor the arm's seam reads as an edge.
    let ring = bridge;
    for (let hop = 0; hop <= SOFT_HOPS && ring.length; hop++) {
      for (const id of ring) soften.set(id, Math.max(soften.get(id) ?? 0, 1 - hop / (SOFT_HOPS + 1)));
      ring = [...new Set(ring.flatMap(id => (around.get(id) ?? []).map(([other]) => other)))].filter(id => !soften.has(id));
    }
    // Over-relaxed Gauss-Seidel on flat arrays.
    const value = new Float64Array(points.length), start: number[] = [0], others: number[] = [], strengths: number[] = [];
    for (const [id, amount] of share) value[id] = amount;
    for (const id of free) {
      let total = 0;
      for (const [, weight] of around.get(id)!) total += weight;
      for (const [other, weight] of around.get(id)!) { others.push(other); strengths.push(weight / total); }
      start.push(others.length);
    }
    for (let pass = 0; pass < FIELD_PASSES; pass++) for (let i = 0; i < free.length; i++) {
      let sum = 0;
      for (let k = start[i]; k < start[i + 1]; k++) sum += value[others[k]] * strengths[k];
      value[free[i]] += FIELD_RELAX * (sum - value[free[i]]);
    }
    const arms = new Set(armGrid.flat()), down = new THREE.Vector3().setFromMatrixPosition(matrix(`forearm.${suffix}`)).sub(pivot).normalize();
    for (const id of free) {
      const spread = THREE.MathUtils.smoothstep(bridgeAlong.get(id) ?? (arms.has(id) ? 1 : 0), .1, .6)
        * THREE.MathUtils.clamp((TURN_TOP - plain[id].clone().sub(pivot).angleTo(down)) / (TURN_TOP - TURN_CONE), 0, 1);
      value[id] = Math.max(THREE.MathUtils.smoothstep(value[id], 0, 1), spread);
    }
    // Relaxed a little, so no single vertex in the armpit runs ahead of its neighbours into a pit. Above and inside the socket,
    // where the collarbone holds the skin up, relaxing would only pull the turn in from the arm and sink it.
    const below = free.map(id => .5 * (1 - THREE.MathUtils.smoothstep(plain[id].y - pivot.y, 0, .02) * THREE.MathUtils.smoothstep(side * (pivot.x - plain[id].x), .02, .04)));
    for (let pass = 0; pass < TURN_PASSES; pass++) for (let i = 0; i < free.length; i++) {
      let sum = 0;
      for (let k = start[i]; k < start[i + 1]; k++) sum += value[others[k]] * strengths[k];
      value[free[i]] += below[i] * (sum - value[free[i]]);
    }
    const support = rigid(`shoulder_skin.${suffix}`);
    for (const id of free) {
      const point = plain[id], towards = point.clone().sub(pivot).normalize(), turn = value[id];
      // Above and inside the socket, rotating around the arm drags the collarbone
      // downward into the chest. Keep that upper surface with the clavicle instead.
      const medial = 1 - THREE.MathUtils.smoothstep(side * point.x, p.neckRadius * 1.05, socket + .07);
      const upper = THREE.MathUtils.smoothstep(point.y, pivot.y + .005, pivot.y + .025);
      const chest = mix(blend(surfaces.torso.weights(point)), rigid(`clavicle.${suffix}`), .9 * medial * upper * THREE.MathUtils.smoothstep(turn, 0, .3));
      const turned = turn < .5 ? mix(chest, support, turn * 2) : mix(support, blend(arm.weights(point)), turn * 2 - 1);
      // A half-turned support would swing the armpit out on an arc into a hanging pouch. Facing the trunk, the skin blends
      // straight from chest to arm instead, mostly linearly skinned, so it stretches across the hollow as the arm rises.
      const armpit = THREE.MathUtils.smoothstep(-side * towards.x, -.3, .5) * (1 - THREE.MathUtils.smoothstep(towards.y, -.15, .1));
      weights[id] = mix(turned, mix(chest, blend(arm.weights(point)), turn), armpit);
      if (armpit) armpits.set(id, armpit * ARMPIT_LINEAR);
    }
  }

  function buildShoulder(suffix: string, side: number, chest: number[], outside: number[][], armGrid: number[][]) {
    const arm = armGrid[armGrid.length - 1], below = armGrid[armGrid.length - 2];
    const shoulder = rigid(`shoulder_skin.${suffix}`);
    const pivot = new THREE.Vector3().setFromMatrixPosition(matrix(`upper_arm.${suffix}`));
    const centre = (ids: number[]) => ids.reduce((sum, id) => sum.add(points[id]), new THREE.Vector3()).divideScalar(ids.length);
    const mouth = centre(chest), end = centre(arm);
    const chestBounds = new THREE.Box3().setFromPoints(chest.map(id => points[id]));
    const armBounds = new THREE.Box3().setFromPoints(arm.map(id => points[id]));
    const direction = -boundaryDirection(chest) * boundaryDirection(arm);
    let target = arm, best = Infinity;
    // Match cross-section directions across the right-angle attachment, preserving boundary winding.
    for (let phase = 0; phase < arm.length; phase++) {
      const candidate = arm.map((_, i) => arm[(phase + direction * i + arm.length) % arm.length]);
      let cost = 0;
      for (let i = 0; i < chest.length; i++) {
        const a = points[chest[i]], b = points[candidate[Math.floor(i * candidate.length / chest.length)]];
        cost += ((a.y - mouth.y) / (chestBounds.max.y - chestBounds.min.y)
          - side * (b.x - end.x) / (armBounds.max.x - armBounds.min.x)) ** 2
          + ((a.z - mouth.z) / (chestBounds.max.z - chestBounds.min.z)
          - (b.z - end.z) / (armBounds.max.z - armBounds.min.z)) ** 2;
      }
      if (cost < best) { best = cost; target = candidate; }
    }
    const curve = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3, t: number) => {
      const u = 1 - t;
      return a.clone().multiplyScalar(u ** 3).addScaledVector(b, 3 * u * u * t)
        .addScaledVector(c, 3 * u * t * t).addScaledVector(d, t ** 3);
    };
    // The chest's own slope at each port vertex, carried on into the hole: the bridge leaves the trunk along it.
    // Grid neighbours rather than mesh edges, whose quad diagonals would lean the two sides differently.
    const slopes = chest.map((id, i) => outside[i].reduce((slope, other) => slope.add(points[id].clone().sub(points[other]).normalize()), new THREE.Vector3()).normalize());
    const cap = (a: THREE.Vector3, slope: THREE.Vector3, b: THREE.Vector3, tangent: THREE.Vector3, t: number, height: number) => {
      const reach = a.distanceTo(b);
      // Only the underside: over the top the cap rounds straight off the collarbone.
      const along = CHEST_SLOPE * (1 - THREE.MathUtils.smoothstep(height, .1, .5));
      const point = curve(a, a.clone().addScaledVector(slope, along * reach).lerp(a.clone().lerp(b, .28), 1 - along / CHEST_SLOPE)
        .add(new THREE.Vector3(side * .025 * p.limbThickness, 0, 0)), b.clone().addScaledVector(tangent, .03 * p.limbThickness), b, t);
      // Zero slope at both seams keeps a widened cap from ending in a sharp
      // inward step where it meets the arm's cross-section.
      const fullness = Math.sin(Math.PI * t) ** 2 * THREE.MathUtils.smoothstep(height, -.25, .45);
      point.x += side * fullness * (.012 + .008 * p.shoulderVolume) * p.limbThickness;
      const unshaped = point.clone(), depth = Math.max(.001, (chestBounds.max.z - chestBounds.min.z + armBounds.max.z - armBounds.min.z) / 4);
      point.z += THREE.MathUtils.clamp((point.z - (mouth.z + end.z) / 2) / depth, -1, 1) * fullness * .004 * p.shoulderDepth;
      point.x += (point.x - pivot.x) * (p.shoulderWidth - 1) * fullness;
      point.y += (point.y - pivot.y) * (p.shoulderHeight - 1) * fullness;
      point.z += (point.z - pivot.z) * (p.shoulderDepth - 1) * fullness;
      return [point, unshaped] as const;
    };
    let previous = chest;
    const capRows: number[][] = [];
    // Rows thin out from the port's count to the arm's, so every stitch between them is made of near-regular triangles
    // instead of fans; the last two match the arm so the seam can be faired column by column.
    const steps = [.125, .25, .375, .5, .625, .75, .875];
    steps.forEach((t, k) => {
      const count = k >= steps.length - 2 ? target.length : Math.round(chest.length * (target.length / chest.length) ** ((k + 1) / (steps.length - 1)));
      const crossSection: THREE.Vector3[] = [], plainSection: THREE.Vector3[] = [];
      const ring = Array.from({ length: count }, (_, i) => {
        const at = i * chest.length / count, low = Math.floor(at), f = at - low;
        const along = i * target.length / count, column = Math.floor(along), g = along - column, next = (column + 1) % target.length;
        const a = points[chest[low]].clone().lerp(points[chest[(low + 1) % chest.length]], f);
        // Between two of the arm's vertices, keep to its round section rather than the chord.
        const first = points[target[column]], second = points[target[next]], b = first.clone().lerp(second, g);
        const radius = THREE.MathUtils.lerp(first.distanceTo(end), second.distanceTo(end), g);
        b.sub(end).setLength(radius).add(end);
        const under = points[below[arm.indexOf(target[column])]].clone().lerp(points[below[arm.indexOf(target[next])]], g);
        const height = ((a.y - mouth.y) / (chestBounds.max.y - chestBounds.min.y)
          + side * (b.x - end.x) / (armBounds.max.x - armBounds.min.x)) / 2;
        const slope = slopes[low].clone().lerp(slopes[(low + 1) % chest.length], f).normalize();
        const [point, unshaped] = cap(a, slope, b, b.clone().sub(under).normalize(), t, height);
        crossSection.push(point); plainSection.push(unshaped);
        // Muscle relief is lit across the bridge from both ends, so the port's edge leaves no line in the shading.
        const relief = (ids: number[], i: number, j: number, amount: number) => light[ids[i]].clone().sub(points[ids[i]]).lerp(light[ids[j]].clone().sub(points[ids[j]]), amount);
        const lit = relief(chest, low, (low + 1) % chest.length, f).lerp(relief(target, column, next, g), THREE.MathUtils.smoothstep(t, 0, 1));
        // The shoulder field below sets the final weights.
        return vertex(point, mix(mix(weights[chest[low]], weights[chest[(low + 1) % chest.length]], f), shoulder, t), lit);
      });
      // Round the rectangular chest port's corners as they enter the deltoid.
      // Both shared attachment loops remain fixed; only the bridge's interior is softened.
      const amount = .5 * Math.sin(Math.PI * t) ** 2, round = (section: THREE.Vector3[]) => {
        for (let pass = 0; pass < Math.round(2 * count / target.length); pass++) section = section.map((point, i, row) => point.clone().lerp(
          row[(i + row.length - 1) % row.length].clone().add(row[(i + 1) % row.length]).multiplyScalar(.5), amount));
        return section;
      };
      const rounded = round(crossSection), plainRounded = round(plainSection);
      for (let i = 0; i < ring.length; i++) {
        const delta = rounded[i].clone().sub(points[ring[i]]);
        points[ring[i]].copy(rounded[i]); plain[ring[i]].copy(plainRounded[i]); light[ring[i]].add(delta);
      }
      for (const id of ring) bridgeAlong.set(id, t);
      stitch(previous, ring, 'arm', true); previous = ring; capRows.push(ring);
    });
    stitch(previous, target, 'arm', true);
    for (const id of target) bridgeAlong.set(id, 1);
    const seamRows = [capRows[capRows.length - 2], capRows[capRows.length - 1], target,
      target.map(id => below[arm.indexOf(id)]), target.map(id => armGrid[armGrid.length - 3][arm.indexOf(id)])];
    const along = [-.03, -.015, 0, .025, .05];
    for (let pass = 0; pass < 2; pass++) for (const [list, lit] of [[points, true], [plain, false]] as const) {
      const original = seamRows.map(row => row.map(id => list[id].clone()));
      for (let row = 1; row < 4; row++) for (let column = 0; column < target.length; column++) {
        const id = seamRows[row][column], fraction = (along[row] - along[row - 1]) / (along[row + 1] - along[row - 1]);
        const amount = row === 2 ? .65 : .25;
        const point = original[row][column].clone().lerp(original[row - 1][column].clone().lerp(original[row + 1][column], fraction), amount);
        if (lit) light[id].add(point.clone().sub(points[id]));
        list[id].copy(point);
      }
    }
    return capRows.flat();
  }
  // The pelvis divides into the thighs along one shared crotch line. Shaped rows carry the trunk's slope down into each thigh's
  // and turn level across the crotch, so the two sides meet in a rounded gusset instead of a crease.
  const pelvis = p.pelvisShape, legGrids = surfaces.legs.map(rows), legTop = Math.max(...legGrids.map(grid => points[grid[grid.length - 1][0]].y));
  const bottom = trunk[0], front = count / 4, back = count * 3 / 4, crotchY = Math.min(Math.max(p.hipsY - pelvis.crotchDrop, legTop + .025), points[trunk[0][0]].y - .02), crotch: number[] = [bottom[front]];
  // The line runs under the pelvis as half a superellipse: it stays forward as it drops from the belly, runs back under the
  // pelvis, then turns up into the seat.
  const curve = (value: number) => Math.sign(value) * Math.abs(value) ** (2 / pelvis.crotchCurve);
  for (let i = 1; i < 8; i++) {
    const t = i / 8, a = points[bottom[front]], b = points[bottom[back]], point = a.clone().lerp(b, t);
    point.z = (a.z + b.z) / 2 + (a.z - b.z) / 2 * curve(Math.cos(t * Math.PI));
    point.y = THREE.MathUtils.lerp(point.y, crotchY, curve(Math.sin(t * Math.PI))); crotch.push(vertex(point, rigid('hips')));
  }
  crotch.push(bottom[back]);
  const left = Array.from({ length: count / 2 + 1 }, (_, i) => bottom[front + i]);
  const right = Array.from({ length: count / 2 + 1 }, (_, i) => bottom[(back + i) % count]);
  const legPorts = [left.concat(crotch.slice(1, -1).reverse()), right.concat(crotch.slice(1, -1))];
  // Each rim vertex leaves the trunk along the trunk's own slope.
  const descent = new Map(bottom.map((id, c) => [id, points[id].clone().sub(points[trunk[1][c]]).normalize()]));
  const thighs = new Map<number, number>(), moulded = new Set([...trunk.filter(row => points[row[0]].y < p.hipsY + .25).flat(), ...crotch]);
  for (let side = 0; side < 2; side++) {
    const grid = legGrids[side], port = legPorts[side], top = align(port, grid[grid.length - 1]), last = grid.length - 1;
    const under = (id: number) => grid[last - 1][grid[last].indexOf(id)], outward = Math.sign(top.reduce((sum, id) => sum + points[id].x, 0));
    tube(grid, 'leg');
    const steps = count < 32 ? 3 : 5, bridge = Array.from({ length: steps }, (_, k) => (k + 1) / (steps + 1)).map(t => port.map((id, i) => {
      const at = i * top.length / port.length, low = Math.floor(at), f = at - low, b0 = top[low], b1 = top[(low + 1) % top.length];
      const a = points[id], b = points[b0].clone().lerp(points[b1], f), length = a.distanceTo(b);
      // Rim vertices split the trunk's slope with the straight way down, so the pelvis does not pinch in before the thigh swells out.
      const rim = descent.get(id), chord = b.clone().sub(a).normalize();
      // Crotch vertices leave level and outward, as far as the room to the inner thigh allows, so both sides meet in a rounded gusset.
      const start = rim ? rim.clone().multiplyScalar(length * .6) : new THREE.Vector3(outward * pelvis.gusset * 3 * Math.max(0, outward * b.x), 0, 0);
      const end = points[under(b0)].clone().lerp(points[under(b1)], f).sub(b).normalize().multiplyScalar(length);
      // Cubic Hermite from the pelvis rim to the top of the thigh.
      const point = a.clone().multiplyScalar(2 * t ** 3 - 3 * t * t + 1).addScaledVector(start, t ** 3 - 2 * t * t + t)
        .addScaledVector(b, 3 * t * t - 2 * t ** 3).addScaledVector(end, t ** 3 - t * t);
      return vertex(point, mix(weights[id], mix(weights[b0], weights[b1], f), THREE.MathUtils.smoothstep(t, 0, 1)));
    }));
    stitch(port, bridge[0], 'leg', true);
    for (let k = 1; k < steps; k++) stitch(bridge[k - 1], bridge[k], 'leg', true);
    stitch(bridge[steps - 1], top, 'leg', true);
    for (const id of [...bridge.flat(), ...grid.flat()]) { moulded.add(id); thighs.set(id, outward); }
    buildFoot(side === 0 ? 'L' : 'R', grid[0]);
  }
  // The seat, hip and inner thighs are moulded over trunk, crotch and thigh alike, so they carry no seam.
  const gauss = (d: number, r: number) => Math.exp(-(d * d) / (r * r)), softPlus = (x: number) => .008 * Math.log1p(Math.exp(x / .008));
  for (const id of moulded) {
    const point = points[id], h = p.hipsY, hx = p.hipHalfWidth, ax = Math.abs(point.x), s = Math.sign(point.x), y = point.y, delta = new THREE.Vector3();
    const behind = THREE.MathUtils.smoothstep(-point.z, 0, .06), ahead = THREE.MathUtils.smoothstep(point.z, 0, .05);
    // Rounded out behind each hip joint, fuller below its centre than above, with a fold where it meets the thigh.
    const gy = h - pelvis.gluteDrop, gx = hx * pelvis.gluteSpread;
    const seat = gauss(ax - gx, .075) * gauss(y - gy, y > gy ? pelvis.gluteHeight : pelvis.gluteHeight * .55);
    const fold = gauss(ax - gx, .07) * gauss(y - (gy - pelvis.gluteHeight * .85), .022);
    delta.z -= behind * (p.glute * .5 * seat - pelvis.gluteFold * fold);
    delta.x += s * pelvis.flare * THREE.MathUtils.smoothstep(ax, hx, hx + .06) * gauss(y - (h - pelvis.flareDrop), .08);
    delta.z += ahead * pelvis.front * gauss(point.x, .045) * gauss(y - (crotchY + .035), .045);
    // Inner thighs: fullness right under the crotch, then the gap, fading out above the knee. The midline stays put.
    const inner = THREE.MathUtils.smoothstep(hx - ax, 0, .05) * THREE.MathUtils.smoothstep(ax, 0, .02), below = THREE.MathUtils.smoothstep(crotchY + .01 - y, 0, .05);
    delta.x += s * inner * below * (pelvis.thighGap * gauss(y - (crotchY - .14), .12) - pelvis.innerThigh * gauss(y - (crotchY - .04), .06));
    // Thighs that meet at rest round off against each other instead of passing through the midline.
    const outward = thighs.get(id);
    if (outward) delta.x = outward * softPlus(outward * (point.x + delta.x)) - point.x;
    point.add(delta); light[id].add(delta);
  }

  function buildHand(suffix: string, side: number, wrist: number[]) {
    const palmName = `palm.${suffix}`, palmMatrix = matrix(palmName), g = p.handScale;
    const levels = [[-.012, .026, .019], [-.03, .037, .017], [-.05, .041, .015], [-.075, .040, .014], [-.09, .038, .012]];
    const front: number[][] = [], back: number[][] = [], local = (x: number, y: number, z: number) => new THREE.Vector3(x * g, y * g, z * g).applyMatrix4(palmMatrix);
    for (const [y, width, depth] of levels) {
      const influence = y > -.02 ? mix(rigid(`hand.${suffix}`), rigid(palmName), .5) : rigid(palmName);
      front.push(Array.from({ length: 9 }, (_, c) => vertex(local((c / 4 - 1) * width, y, .002 + depth), influence)));
      back.push(Array.from({ length: 9 }, (_, c) => vertex(local((c / 4 - 1) * width, y, .002 - depth), influence)));
    }
    for (let r = 0; r < levels.length - 1; r++) {
      // The wrist matcher reads these boundary directions before the final winding pass.
      // Opposing palm faces and side walls must already point outward.
      for (let c = 0; c < 8; c++) {
        quad(front[r][c], front[r + 1][c], front[r + 1][c + 1], front[r][c + 1], 'hand');
        quad(back[r][c + 1], back[r + 1][c + 1], back[r + 1][c], back[r][c], 'hand');
      }
      for (const c of [0, 8]) if (!(c === (side < 0 ? 0 : 8) && r === 1)) {
        if (c === 0) quad(front[r][c], back[r][c], back[r + 1][c], front[r + 1][c], 'hand');
        else quad(front[r + 1][c], back[r + 1][c], back[r][c], front[r][c], 'hand');
      }
    }
    join(wrist, front[0].concat([...back[0]].reverse()), 'hand');
    for (let finger = 0; finger < 4; finger++) {
      const name = ['index', 'middle', 'ring', 'pinky'][finger], c = side < 0 ? finger * 2 : (3 - finger) * 2;
      const last = levels.length - 1, mouth = front[last].slice(c, c + 3).concat(back[last].slice(c, c + 3).reverse());
      const length = [.036, .04, .037, .029][finger] * g, radius = [.0094, .0097, .0091, .008][finger] * g;
      digit([1, 2, 3].map(i => `finger_${name}.${i}.${suffix}`), [length * .7, length * .7, length * .62], radius, mouth, palmName);
    }
    const c = side < 0 ? 0 : 8;
    digit([`thumb.1.${suffix}`, `thumb.2.${suffix}`], [.034 * g, .026 * g], .0118 * g,
      [front[1][c], back[1][c], back[2][c], front[2][c]], palmName);
  }

  function digit(names: string[], lengths: number[], radius: number, mouth: number[], parent: string) {
    let previous = mouth;
    for (let segment = 0; segment < names.length; segment++) {
      const name = names[segment], transform = matrix(name), length = lengths[segment];
      for (const t of [.12, .5, .88]) {
        const r = radius * (1 - segment * .11), influence = t < .5
          ? mix(rigid(segment ? names[segment - 1] : parent), rigid(name), .5 + t)
          : segment < names.length - 1 ? mix(rigid(name), rigid(names[segment + 1]), Math.max(0, (t - .65) * .6)) : rigid(name);
        const ring = Array.from({ length: 8 }, (_, i) => {
          const a = i / 8 * Math.PI * 2;
          return vertex(new THREE.Vector3(Math.cos(a) * r, -length * t, Math.sin(a) * r).applyMatrix4(transform), influence);
        });
        if (!segment && t === .12) join(previous, ring, 'hand'); else stitch(previous, ring, 'hand');
        previous = ring;
      }
    }
    const end = vertex(new THREE.Vector3(0, -lengths[lengths.length - 1] - radius * .45, 0).applyMatrix4(matrix(names[names.length - 1])), rigid(names[names.length - 1]));
    for (let i = 0; i < previous.length; i++) face(previous[i], previous[(i + 1) % previous.length], end, 'hand');
  }

  function buildFoot(suffix: string, ankle: number[]) {
    const transform = matrix(`foot.${suffix}`), ground = -p.ankleY, wide = (1 + p.limbThickness) / 2;
    // The top of the foot follows the sole, so a different ankle height moves the foot without squashing or inverting it.
    const sink = p.ankleY - (p.footAnkleY ?? p.ankleY);
    const keys = [[-.078, .014, -.07, ground + .02], [-.068, .028, -.035, ground + .002], [-.045, .034, .002, ground], [-.01, .036, .012, ground], [.035, .04, -.022, ground],
      [.065, .044, -.046, ground], [.09, .046, -.060, ground], [.115, .045, -.070, ground], [.14, .041, -.078, ground], [.16, .036, -.081, ground + .001], [.176, .016, -.088, ground + .006]];
    const grid = keys.map(([z, width, top, bottom]) => Array.from({ length: 16 }, (_, c) => {
      const a = c / 16 * Math.PI * 2, shape = (v: number) => Math.sign(v) * Math.abs(v) ** (2 / 2.6);
      const point = new THREE.Vector3(shape(Math.cos(a)) * width * wide, (top - sink + bottom) / 2 - shape(Math.sin(a)) * (top - sink - bottom) / 2, z).applyMatrix4(transform);
      return vertex(point, mix(rigid(`foot.${suffix}`), rigid(`toe.${suffix}`), THREE.MathUtils.smoothstep(z, .065, .115)));
    }));
    tube(grid, 'foot', (r, c) => r >= 2 && r < 4 && c >= 10 && c < 14);
    cap(grid[0], 'foot'); cap(grid[grid.length - 1], 'foot');
    join(ankle, port(grid, 2, 4, 10, 14), 'foot');
  }

  // Every neighbouring face must traverse its shared edge in opposite directions.
  orient(triangles, points);
  for (let t = 0; t < regions.length; t++) if (regions[t] === 'trunk'
    && (points[triangles[t * 3]].y + points[triangles[t * 3 + 1]].y + points[triangles[t * 3 + 2]].y) / 3 > p.shoulderY + HUMAN_NECK_BASE_RISE) regions[t] = 'neck';
  const used = new Map<number, number>(), positions: number[] = [], lighting: number[] = [], indices: number[] = [], skinIndices: number[] = [], skinWeights: number[] = [], volume: number[] = [];
  for (const old of triangles) {
    if (!used.has(old)) {
      used.set(old, used.size); positions.push(...points[old].toArray()); lighting.push(...light[old].toArray());
      const influences = weights[old], sum = influences.reduce((n, [, weight]) => n + weight, 0);
      const support = influences.reduce((n, [bone, weight]) => n + (skeleton.bones[bone].userData.nimblezSkinSupport ? weight : 0), 0);
      const upperArm = influences.reduce((n, [bone, weight]) => n + (skeleton.bones[bone].name.startsWith('upper_arm.') ? weight : 0), 0);
      volume.push(THREE.MathUtils.smoothstep(support + upperArm, 0, .1) * (1 - (armpits.get(old) ?? 0)));
      for (let i = 0; i < 4; i++) { skinIndices.push(influences[i]?.[0] ?? 0); skinWeights.push((influences[i]?.[1] ?? 0) / sum); }
    }
    indices.push(used.get(old)!);
  }
  const along = new Float32Array(used.size);
  for (const [old, id] of used) along[id] = bridgeAlong.get(old) ?? 0;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(lighting, 3)); geometry.setIndex(indices); geometry.computeVertexNormals();
  softenNormals(geometry, new Map([...soften].flatMap(([old, amount]) => used.has(old) ? [[used.get(old)!, amount] as const] : [])));
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(used.size * 2), 2));
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndices, 4)); geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeights, 4));
  geometry.setAttribute(SKIN_VOLUME, new THREE.Float32BufferAttribute(volume, 1));
  if (options.cacheKey) {
    cache.set(options.cacheKey, { geometry: geometry.clone(), regions, shoulders: bridges, along });
    if (cache.size > CACHE_LIMIT) { const key = cache.keys().next().value!; cache.get(key)!.geometry.dispose(); cache.delete(key); }
  }
  return { geometry, regions, shoulders: bridges, along };
}

function bodyResult(geometry: THREE.BufferGeometry, regions: Region[], shoulders: Set<number>, along: Float32Array, socketZ: number): ConnectedBody {
  // On its own, a shoulder bridge stops a short way out from the chest, at a ring around the bridge.
  const trunkSide = { values: along, below: SHOULDER_COVER };
  // The cover ring as segments, one across each bridge face it passes through.
  const index = geometry.index!, position = geometry.getAttribute('position'), ring: [THREE.Vector3, THREE.Vector3][] = [];
  for (const face of shoulders) {
    const points: THREE.Vector3[] = [];
    for (let c = 0; c < 3; c++) {
      const a = index.getX(face * 3 + c), b = index.getX(face * 3 + (c + 1) % 3), da = along[a] - SHOULDER_COVER, db = along[b] - SHOULDER_COVER;
      if ((da < 0) !== (db < 0)) { const t = da / (da - db); points.push(new THREE.Vector3(Math.abs(THREE.MathUtils.lerp(position.getX(a), position.getX(b), t)), THREE.MathUtils.lerp(position.getY(a), position.getY(b), t), THREE.MathUtils.lerp(position.getZ(a), position.getZ(b), t) - socketZ)); }
    }
    if (points.length === 2) ring.push([points[0], points[1]]);
  }
  const coverAt = (y: number, back: boolean) => {
    let reach = Infinity;
    for (const [a, b] of ring) if ((a.y < y) !== (b.y < y) && (a.z + b.z < 0) === back) reach = Math.min(reach, THREE.MathUtils.lerp(a.x, b.x, (y - a.y) / (b.y - a.y)));
    return reach;
  };
  return { geometry, coverAt, garment: (selected, from, to, gap, side, cuts) => taped('garment', () => cutBodyGarment(geometry,
    regions.flatMap((region, face) => selected.includes(region) || (shoulders.has(face) && selected.includes('shoulder')) ? [face] : []), from, to, gap, side, cuts,
    selected.includes('shoulder') && !selected.includes('arm') ? trunkSide : undefined)), dress: (materials, clearances, hidden = {}) => {
    const roles = new Set(Object.values(materials)), hides = Object.keys(hidden).length > 0;
    if (roles.size === 1 && !hides && !Object.values(clearances).some(amount => amount > 0)) return [{ material: roles.values().next().value!, geometry }];
    const index = geometry.index!, position = geometry.getAttribute('position'), normal = geometry.getAttribute('normal');
    const grow = new Float32Array(position.count), batches = new Map<THREE.Material, number[]>();
    for (let t = 0; t < regions.length; t++) {
      const region = regions[t], material = materials[region], ids = batches.get(material) ?? [], above = hidden[region];
      if (above !== undefined && (position.getY(index.getX(t * 3)) + position.getY(index.getX(t * 3 + 1)) + position.getY(index.getX(t * 3 + 2))) / 3 > above) continue;
      for (let c = 0; c < 3; c++) { const id = index.getX(t * 3 + c); ids.push(id); grow[id] = Math.max(grow[id], clearances[region] ?? 0); }
      batches.set(material, ids);
    }
    for (let i = 0; i < position.count; i++) if (grow[i]) position.setXYZ(i, position.getX(i) + normal.getX(i) * grow[i], position.getY(i) + normal.getY(i) * grow[i], position.getZ(i) + normal.getZ(i) * grow[i]);
    if (batches.size === 1 && !hides) return [{ material: batches.keys().next().value!, geometry }];
    return [...batches].map(([material, selected]) => ({ material, geometry: subset(geometry, selected) }));
  } };
}

function subset(geometry: THREE.BufferGeometry, selected: number[]) {
  const part = new THREE.BufferGeometry(), remap = new Map<number, number>(), source: number[] = [];
  part.setIndex(selected.map(id => { if (!remap.has(id)) { remap.set(id, remap.size); source.push(id); } return remap.get(id)!; }));
  for (const [name, attribute] of Object.entries(geometry.attributes)) {
    const values = name === 'skinIndex' ? new Uint16Array(source.length * attribute.itemSize) : new Float32Array(source.length * attribute.itemSize);
    for (let i = 0; i < source.length; i++) for (let c = 0; c < attribute.itemSize; c++) values[i * attribute.itemSize + c] = attribute.getComponent(source[i], c);
    part.setAttribute(name, new THREE.BufferAttribute(values, attribute.itemSize));
  }
  return part;
}

/** Average each listed vertex's normal with its neighbours' by its amount. */
function softenNormals(geometry: THREE.BufferGeometry, amounts: Map<number, number>) {
  const index = geometry.index!, normal = geometry.getAttribute('normal') as THREE.BufferAttribute, around = new Map<number, Set<number>>([...amounts.keys()].map(id => [id, new Set()]));
  for (let t = 0; t < index.count; t += 3) for (let c = 0; c < 3; c++) {
    const a = index.getX(t + c), b = index.getX(t + (c + 1) % 3);
    around.get(a)?.add(b); around.get(b)?.add(a);
  }
  const value = new THREE.Vector3(), sum = new THREE.Vector3();
  for (let pass = 0; pass < SOFT_PASSES; pass++) {
    const next = [...amounts].map(([id, amount]) => {
      sum.set(0, 0, 0);
      for (const other of around.get(id)!) sum.add(value.fromBufferAttribute(normal, other));
      return [id, value.fromBufferAttribute(normal, id).lerp(sum.normalize(), amount).normalize().toArray()] as const;
    });
    for (const [id, [x, y, z]] of next) normal.setXYZ(id, x, y, z);
  }
}

function orient(indices: number[], points: THREE.Vector3[]) {
  const edges = new Map<string, { triangle: number; forward: boolean }[]>(), neighbours: [number, boolean][][] = Array.from({ length: indices.length / 3 }, () => []);
  for (let t = 0; t < indices.length / 3; t++) for (let c = 0; c < 3; c++) {
    const a = indices[t * 3 + c], b = indices[t * 3 + (c + 1) % 3], key = a < b ? `${a}:${b}` : `${b}:${a}`;
    const uses = edges.get(key) ?? []; uses.push({ triangle: t, forward: a < b }); edges.set(key, uses);
  }
  for (const uses of edges.values()) if (uses.length === 2) {
    const [a, b] = uses, flip = a.forward === b.forward;
    neighbours[a.triangle].push([b.triangle, flip]); neighbours[b.triangle].push([a.triangle, flip]);
  }
  const state = new Int8Array(neighbours.length).fill(-1), cross = new THREE.Vector3();
  for (let first = 0; first < state.length; first++) if (state[first] < 0) {
    const pending = [first]; state[first] = 0;
    for (let i = 0; i < pending.length; i++) for (const [next, flip] of neighbours[pending[i]]) if (state[next] < 0) { state[next] = state[pending[i]] ^ Number(flip); pending.push(next); }
    let volume = 0;
    for (const t of pending) {
      const [a, b, c] = indices.slice(t * 3, t * 3 + 3).map(i => points[i]);
      volume += a.dot(cross.crossVectors(b, c)) * (state[t] ? -1 : 1);
    }
    for (const t of pending) if (Boolean(state[t]) !== (volume < 0)) { const b = indices[t * 3 + 1]; indices[t * 3 + 1] = indices[t * 3 + 2]; indices[t * 3 + 2] = b; }
  }
}
